import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import StandardModal from 'components/general/defaultModals/StandardModal';
import useSortableList from 'components/general/useSortableList';
import { getContestPluginConfig } from '../../services/contestPlugin';
import { getUserInitials, resolveAvatarSrc } from 'utils/avatar';
import { getSocket } from '../../socket';

export default function ContestJudgeModal({ meeting, show, onClose, panelMode = 'judge' }) {
  const { user: authUser } = useSelector((state) => state.auth);
  const url = process.env.REACT_APP_URL;
  const meetingId = meeting?.meetingDetails?.id || meeting?.meetingDetails?._id;
  const [contestTypes, setContestTypes] = useState([]);
  const [assignedContestEntries, setAssignedContestEntries] = useState([]);
  const [activeContestTypeIndex, setActiveContestTypeIndex] = useState(0);
  const [activeAgendaIndexByContest, setActiveAgendaIndexByContest] = useState({});
  const [ballotValues, setBallotValues] = useState({});
  const ballotValuesRef = useRef({});
  const [scoreSaveState, setScoreSaveState] = useState({ status: 'idle', message: '' });
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [infoCriterion, setInfoCriterion] = useState(null);
  const [panelView, setPanelView] = useState('speakers');
  const [resultsOrderByContest, setResultsOrderByContest] = useState({});
  const [rankingSubmitState, setRankingSubmitState] = useState({ status: 'idle', message: '' });
  const [rankingSubmitted, setRankingSubmitted] = useState(false);
  const nextAgendaButtonRef = useRef(null);
  const isTiebreakerPanel = panelMode === 'tiebreaker';
  const isMeetingLocked = Boolean(meeting?.meetingDetails?.isLocked);
  const [submissionStatusVersion, setSubmissionStatusVersion] = useState(0);

  const getStorageKeyForOrder = (mId, cType, uId) => {
    if (!mId || !cType || !uId) return '';
    return `contesthub_results_order_${mId}_${cType}_${uId}`;
  };

  const buildBallotKey = (contestType, agendaItemId, categoryId, criterionId) => `${contestType}:${agendaItemId}:${categoryId}:${criterionId}`;
  const buildStoredCriterionKey = (categoryId, criterionId) => `${categoryId}:${criterionId}`;

  const authIdentityIds = useMemo(() => new Set([
    String(authUser?.user?._id || '').trim(),
    String(authUser?.user?.id || '').trim(),
    String(authUser?.user?.tmAuthUserId || '').trim(),
    String(authUser?.user?.localUserId || '').trim(),
    String(authUser?._id || '').trim(),
    String(authUser?.id || '').trim(),
    String(authUser?.tmAuthUserId || '').trim(),
  ].filter(Boolean)), [authUser]);
  const currentJudgeId = useMemo(
    () => String(authUser?.user?._id || authUser?.user?.id || authUser?.user?.tmAuthUserId || authUser?._id || authUser?.tmAuthUserId || '').trim(),
    [authUser?._id, authUser?.tmAuthUserId, authUser?.user?._id, authUser?.user?.id, authUser?.user?.tmAuthUserId]
  );

  useEffect(() => {
    if (!show || !authUser?.token || !meetingId) return;

    Promise.all([
      fetch(`${url}/contesthub/meetings/${meetingId}/setup`, { headers: { Authorization: `Bearer ${authUser.token}` } }).then((response) => response.ok ? response.json() : null),
      getContestPluginConfig(authUser.token)
    ]).then(([setup, config]) => {
      const contestTypeConfig = Array.isArray(config?.contestTypes) ? config.contestTypes : [];
      const contestJudges = Array.isArray(setup?.contestJudges) ? setup.contestJudges : [];

      const judgeContestEntries = contestJudges
        .filter((entry) => {
          const chiefJudge = String(entry?.chiefJudge || '').trim();
          const tiebreakingJudge = String(entry?.tiebreakingJudge || '').trim();
          const judges = Array.isArray(entry?.judges) ? entry.judges.map((judge) => String(judge || '').trim()) : [];
          if (isTiebreakerPanel) return authIdentityIds.has(tiebreakingJudge);
          return authIdentityIds.has(chiefJudge)
            || authIdentityIds.has(tiebreakingJudge)
            || judges.some((judgeId) => authIdentityIds.has(judgeId));
        })
        .map((entry) => ({
          contestType: String(entry?.contestType || '').trim(),
          agendaItemIds: Array.isArray(entry?.agendaItemIds)
            ? entry.agendaItemIds.map((id) => String(id || '').trim()).filter(Boolean)
            : []
        }))
        .filter((entry) => Boolean(entry.contestType));

      setContestTypes(contestTypeConfig);
      setAssignedContestEntries(judgeContestEntries);
      setActiveContestTypeIndex((current) => {
        const nextIndex = Number.isFinite(current) ? current : 0;
        return judgeContestEntries.length > 0 ? Math.min(nextIndex, judgeContestEntries.length - 1) : 0;
      });
    }).catch(() => {
      setContestTypes([]);
      setAssignedContestEntries([]);
      setActiveContestTypeIndex(0);
      setActiveAgendaIndexByContest({});
    });
  }, [authIdentityIds, authUser?.token, isTiebreakerPanel, meetingId, show, url]);

  const agendaEntries = useMemo(() => {
    if (Array.isArray(meeting?.items) && meeting.items.length > 0) return meeting.items;
    if (Array.isArray(meeting?.meetingDetails?.items) && meeting.meetingDetails.items.length > 0) return meeting.meetingDetails.items;
    if (Array.isArray(meeting?.meetingDetails?.agenda) && meeting.meetingDetails.agenda.length > 0) return meeting.meetingDetails.agenda;
    return [];
  }, [meeting?.items, meeting?.meetingDetails?.agenda, meeting?.meetingDetails?.items]);

  const agendaCandidates = useMemo(() => {
    const entries = Array.isArray(agendaEntries) ? agendaEntries : [];
    return entries
      .map((item, index) => {
        const explicitId = String(item?._id || item?.id || item?.agendaItemId || item?.itemId || '').trim();
        const fallbackId = `${meetingId || 'meeting'}:${index}:${String(item?.agendaEvent?._id || item?.agendaEvent?.name || 'agenda').trim()}`;
        const id = explicitId || fallbackId;
        const titleLine = String(item?.item_title || '').split('\n').map((line) => line.trim()).find(Boolean) || '';
        const eventName = String(item?.agendaEvent?.name || '').trim() || `Agenda item ${index + 1}`;
        const speechTitle = titleLine || eventName;
        const speakerName = `${item?.assignedUser?.firstName || ''} ${item?.assignedUser?.lastName || ''}`.trim() || 'TBA';
        const avatarSrc = resolveAvatarSrc(item?.assignedUser?.avatarUrl || item?.assignedUser?.avatar || '');
        const initials = getUserInitials(item?.assignedUser, speakerName);
        return {
          id,
          item,
          speechTitle,
          speakerName,
          avatarSrc,
          initials
        };
      })
      .filter((entry) => Boolean(entry.id));
  }, [agendaEntries, meetingId]);

  const agendaCandidateById = useMemo(() => new Map(agendaCandidates.map((entry) => [entry.id, entry])), [agendaCandidates]);

  const contestTypeNameFor = (contestTypeId) => {
    const match = contestTypes.find((type) => type?._id === contestTypeId || type?.id === contestTypeId);
    return match?.name || contestTypeId;
  };

  const scoreByCriterionKey = useMemo(() => {
    const values = {};
    Object.entries(ballotValues).forEach(([key, raw]) => {
      const asNumber = Number(raw);
      if (Number.isFinite(asNumber)) values[key] = asNumber;
    });
    return values;
  }, [ballotValues]);

  const hasContestTypePager = assignedContestEntries.length > 1;
  const selectedContestEntry = assignedContestEntries[activeContestTypeIndex] || null;
  const selectedContestType = selectedContestEntry?.contestType || '';
  const selectedContestAgendaItems = (selectedContestEntry?.agendaItemIds || [])
    .map((id) => agendaCandidateById.get(id))
    .filter(Boolean)
    .sort((left, right) => Number(left?.item?.position || 0) - Number(right?.item?.position || 0));
  const activeAgendaIndex = activeAgendaIndexByContest[selectedContestType] ?? 0;
  const selectedAgendaItem = selectedContestAgendaItems[activeAgendaIndex] || null;
  const hasAgendaPager = selectedContestAgendaItems.length > 1;
  const selectedContestConfig = contestTypes.find((type) => (type?._id || type?.id) === selectedContestType) || null;
  const ballotCategories = useMemo(
    () => (Array.isArray(selectedContestConfig?.categories) ? selectedContestConfig.categories : []),
    [selectedContestConfig]
  );

  useEffect(() => {
    if (!show || !authUser?.token || !meetingId || !selectedContestType) {
      setRankingSubmitted(false);
      return undefined;
    }

    let cancelled = false;
    const refreshSubmissionStatus = async () => {
      try {
        const submissionEndpoint = isTiebreakerPanel ? 'tiebreaker-ranking/submission' : 'rankings/submission';
        const response = await fetch(
          `${url}/contesthub/meetings/${meetingId}/${submissionEndpoint}?${new URLSearchParams({ contestType: selectedContestType }).toString()}`,
          { headers: { Authorization: `Bearer ${authUser.token}` } }
        );
        if (!response.ok || cancelled) return;
        const payload = await response.json();
        const submitted = Boolean(payload?.submitted);
        setRankingSubmitted(submitted);

        const submittedOrder = Array.isArray(payload?.topAgendaItemIds)
          ? payload.topAgendaItemIds
          : Array.isArray(payload?.orderedAgendaItemIds)
            ? payload.orderedAgendaItemIds
            : [];
        const storageKey = getStorageKeyForOrder(meetingId, selectedContestType, currentJudgeId);

        let localOrder = null;
        if (storageKey) {
          try {
            const raw = localStorage.getItem(storageKey);
            if (raw) localOrder = JSON.parse(raw);
          } catch (_) {}
        }

        const effectiveOrder = (Array.isArray(localOrder) && localOrder.length > 0)
          ? localOrder
          : (Array.isArray(submittedOrder) && submittedOrder.length > 0)
            ? submittedOrder
            : null;

        if (effectiveOrder) {
          setResultsOrderByContest((current) => {
            if (current[selectedContestType] && current[selectedContestType].length > 0) return current;
            return { ...current, [selectedContestType]: effectiveOrder };
          });
        }

        if (submitted) {
          setRankingSubmitState((current) => current.status === 'saving'
            ? current
            : { status: 'success', message: isTiebreakerPanel ? 'Ranking submitted.' : 'Top 3 submitted.' });
        } else {
          setRankingSubmitState((current) => current.status === 'saving'
            ? current
            : { status: 'idle', message: '' });
        }
      } catch (_) {}
    };

    setRankingSubmitted(false);
    refreshSubmissionStatus();
    const refreshInterval = window.setInterval(refreshSubmissionStatus, 5000);
    window.addEventListener('focus', refreshSubmissionStatus);

    return () => {
      cancelled = true;
      window.clearInterval(refreshInterval);
      window.removeEventListener('focus', refreshSubmissionStatus);
    };
  }, [authUser?.token, currentJudgeId, isTiebreakerPanel, meetingId, selectedContestType, show, submissionStatusVersion, url]);

  useEffect(() => {
    if (!show || !meetingId || !selectedContestType) return undefined;
    const socket = getSocket();
    const expectedRankingType = isTiebreakerPanel ? 'tiebreaker' : 'judge';
    const handleRankingUpdated = (payload) => {
      if (
        String(payload?.meetingId || '') === String(meetingId)
        && String(payload?.contestType || '') === selectedContestType
        && payload?.rankingType === expectedRankingType
      ) {
        setSubmissionStatusVersion((current) => current + 1);
      }
    };
    socket.on('contesthub:ranking-updated', handleRankingUpdated);
    socket.emit('meeting:join', String(meetingId));
    return () => socket.off('contesthub:ranking-updated', handleRankingUpdated);
  }, [isTiebreakerPanel, meetingId, selectedContestType, show]);

  const timerColors = useMemo(() => {
    const convertToSeconds = (timeString) => {
      if (!timeString || typeof timeString !== 'string') return null;
      const [minutes, seconds] = timeString.split(':').map(Number);
      if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) return null;
      return minutes * 60 + seconds;
    };
    const greenTime = convertToSeconds(selectedAgendaItem?.item?.agendaEvent?.green);
    const amberTime = convertToSeconds(selectedAgendaItem?.item?.agendaEvent?.amber);
    const redTime = convertToSeconds(selectedAgendaItem?.item?.agendaEvent?.red);

    if (redTime !== null && timerSeconds >= redTime + 30) return { background: '#dc3544', foreground: '#ffffff' };
    if (redTime !== null && timerSeconds >= redTime) return { background: '#dc3545', foreground: '#ffffff' };
    if (amberTime !== null && timerSeconds >= amberTime) return { background: '#ffc107', foreground: '#000000' };
    if (greenTime !== null && timerSeconds >= greenTime) return { background: '#146c43', foreground: '#ffffff' };
    return { background: 'var(--modal-bg)', foreground: 'var(--foreground)' };
  }, [selectedAgendaItem?.item?.agendaEvent, timerSeconds]);

  const formattedTimer = useMemo(() => {
    const minutes = Math.floor(timerSeconds / 60);
    const seconds = timerSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }, [timerSeconds]);

  const timerProgress = useMemo(() => {
    const redValue = selectedAgendaItem?.item?.agendaEvent?.red;
    if (!redValue || typeof redValue !== 'string') return 0;
    const [minutes, seconds] = redValue.split(':').map(Number);
    const maximumSeconds = (minutes * 60) + seconds;
    if (!Number.isFinite(maximumSeconds) || maximumSeconds <= 0) return 0;
    return Math.min(100, Math.round((timerSeconds / maximumSeconds) * 100));
  }, [selectedAgendaItem?.item?.agendaEvent?.red, timerSeconds]);

  const timerRingBackground = useMemo(() => {
    const convertToSeconds = (timeString) => {
      if (!timeString || typeof timeString !== 'string') return null;
      const [minutes, seconds] = timeString.split(':').map(Number);
      if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) return null;
      return (minutes * 60) + seconds;
    };
    const greenTime = convertToSeconds(selectedAgendaItem?.item?.agendaEvent?.green);
    const amberTime = convertToSeconds(selectedAgendaItem?.item?.agendaEvent?.amber);
    const redTime = convertToSeconds(selectedAgendaItem?.item?.agendaEvent?.red);
    if (!redTime || redTime <= 0) return undefined;

    const greenProgress = Math.min(100, Math.max(0, (greenTime || 0) / redTime * 100));
    const amberProgress = Math.min(100, Math.max(greenProgress, (amberTime || greenTime || 0) / redTime * 100));
    const progress = Math.min(100, Math.max(0, timerSeconds / redTime * 100));
    if (progress >= 100) return 'conic-gradient(from 0deg, #dc3545 0% 100%)';
    if (progress <= 0) return 'conic-gradient(from 0deg, rgba(255, 255, 255, 0.16) 0% 100%)';

    const colorStops = [
      { color: '#56d3ff', position: 0 },
      { color: '#146c43', position: greenProgress },
      { color: '#ffc107', position: amberProgress },
      { color: '#dc3545', position: 100 },
    ].filter((stop) => stop.position < progress || stop.position === 0);
    const stops = colorStops.flatMap((stop, index) => {
      const nextPosition = colorStops[index + 1]?.position ?? progress;
      const endPosition = Math.min(progress, nextPosition);
      return [`${stop.color} ${stop.position}%`, `${stop.color} ${endPosition}%`];
    });
    stops.push(`rgba(255, 255, 255, 0.16) ${progress}%`, 'rgba(255, 255, 255, 0.16) 100%');
    return `conic-gradient(from 0deg, ${stops.join(', ')})`;
  }, [selectedAgendaItem?.item?.agendaEvent, timerSeconds]);

  useEffect(() => {
    const agendaItemIds = new Set([
      selectedAgendaItem?.id,
      selectedAgendaItem?.item?.agendaItemId,
      selectedAgendaItem?.item?._id,
      selectedAgendaItem?.item?.id,
    ].map((value) => String(value || '').trim()).filter(Boolean));
    const agendaItemId = String(selectedAgendaItem?.item?.agendaItemId || selectedAgendaItem?.id || '').trim();
    if (!show || !meetingId || !agendaItemId) {
      setTimerSeconds(0);
      return undefined;
    }

    setTimerSeconds(Number(selectedAgendaItem?.item?.timer?.time || 0));
    const socket = getSocket();
    const meetingRoomId = String(meetingId);

    const handleTimerTick = (payload) => {
      if (String(payload?.meetingId || '') !== meetingRoomId || !agendaItemIds.has(String(payload?.agendaItemId || '').trim())) return;
      const nextTime = Number(payload?.time);
      if (Number.isFinite(nextTime)) setTimerSeconds(Math.max(0, nextTime));
    };
    const handleTimerReset = (event) => {
      if (String(event?.detail?.meetingId || meetingRoomId) !== meetingRoomId) return;
      if (agendaItemIds.has(String(event?.detail?.agendaItemId || '').trim())) setTimerSeconds(0);
    };
    const handleSocketTimerReset = (payload) => {
      if (String(payload?.meetingId || '') === meetingRoomId && agendaItemIds.has(String(payload?.agendaItemId || '').trim())) {
        setTimerSeconds(0);
      }
    };
    const handleTimerState = (payload) => {
      if (String(payload?.meetingId || '') !== meetingRoomId || !agendaItemIds.has(String(payload?.agendaItemId || '').trim())) return;
      const nextTime = Number(payload?.time);
      if (Number.isFinite(nextTime)) setTimerSeconds(Math.max(0, nextTime));
    };

    socket.on('meeting:timer-tick', handleTimerTick);
    socket.on('meeting:timer-reset', handleSocketTimerReset);
    socket.on('meeting:timer-state', handleTimerState);
    window.addEventListener('meeting:timer-reset', handleTimerReset);
    socket.emit('meeting:join', meetingRoomId);

    return () => {
      socket.off('meeting:timer-tick', handleTimerTick);
      socket.off('meeting:timer-reset', handleSocketTimerReset);
      socket.off('meeting:timer-state', handleTimerState);
      window.removeEventListener('meeting:timer-reset', handleTimerReset);
    };
  }, [meetingId, selectedAgendaItem?.id, selectedAgendaItem?.item?._id, selectedAgendaItem?.item?.agendaItemId, selectedAgendaItem?.item?.id, selectedAgendaItem?.item?.timer?.time, show, url]);

  const loadScoresForActiveAgenda = useCallback(() => {
    if (!show || !authUser?.token || !meetingId || !selectedContestType || !selectedAgendaItem?.id) return;

    const query = new URLSearchParams({
      contestType: selectedContestType,
      agendaItemId: selectedAgendaItem.id
    });

    fetch(`${url}/contesthub/meetings/${meetingId}/scores?${query.toString()}`, {
      headers: { Authorization: `Bearer ${authUser.token}` }
    })
      .then((response) => response.ok ? response.json() : { scores: [] })
      .then((payload) => {
        const contextPrefix = `${selectedContestType}:${selectedAgendaItem.id}:`;
        const scores = Array.isArray(payload?.scores) ? payload.scores : [];
        const preferred = scores.find((entry) => (
          authIdentityIds.has(String(entry?.judgeTmAuthUserId || '').trim()) ||
          authIdentityIds.has(String(entry?.judgeLocalUserId || '').trim())
        )) || null;
        const scoreValues = preferred?.scoreValues && typeof preferred.scoreValues === 'object'
          ? preferred.scoreValues
          : {};

        const hydratedValues = Object.entries(scoreValues).reduce((acc, [storedKey, rawValue]) => {
          const key = String(storedKey || '').trim();
          const separatorIndex = key.indexOf(':');
          const parsed = Number(rawValue);
          if (separatorIndex <= 0 || !Number.isFinite(parsed)) return acc;
          const categoryId = key.slice(0, separatorIndex);
          const criterionId = key.slice(separatorIndex + 1);
          const ballotKey = buildBallotKey(selectedContestType, selectedAgendaItem.id, categoryId, criterionId);
          acc[ballotKey] = String(parsed);
          return acc;
        }, {});

        setBallotValues((current) => {
          const withoutActiveContext = Object.entries(current).reduce((acc, [key, value]) => {
            if (!key.startsWith(contextPrefix)) acc[key] = value;
            return acc;
          }, {});
          const next = { ...withoutActiveContext, ...hydratedValues };
          ballotValuesRef.current = next;
          return next;
        });
      })
      .catch(() => {});
  }, [authIdentityIds, authUser?.token, meetingId, selectedAgendaItem?.id, selectedContestType, show, url]);

  const saveScoresForActiveAgenda = useCallback(async ({ showStatus = false } = {}) => {
    if (!authUser?.token || !meetingId || !selectedContestType || !selectedAgendaItem?.id) return false;
    if (showStatus) setScoreSaveState({ status: 'saving', message: '' });
    const contextPrefix = `${selectedContestType}:${selectedAgendaItem.id}:`;
    const scoreValues = Object.entries(ballotValuesRef.current).reduce((acc, [key, rawValue]) => {
      if (!key.startsWith(contextPrefix)) return acc;
      const scopedKey = key.slice(contextPrefix.length);
      const separatorIndex = scopedKey.indexOf(':');
      const parsed = Number(rawValue);
      if (separatorIndex <= 0 || !Number.isFinite(parsed)) return acc;
      const categoryId = scopedKey.slice(0, separatorIndex);
      const criterionId = scopedKey.slice(separatorIndex + 1);
      acc[buildStoredCriterionKey(categoryId, criterionId)] = parsed;
      return acc;
    }, {});

    try {
      const response = await fetch(`${url}/contesthub/meetings/${meetingId}/scores`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${authUser.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contestType: selectedContestType,
          agendaItemId: selectedAgendaItem.id,
          scoreValues
        })
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.Error || payload?.error || 'Scores could not be saved.');
      }
      if (showStatus) setScoreSaveState({ status: 'success', message: 'All scores saved.' });
      return true;
    } catch (error) {
      if (showStatus) setScoreSaveState({ status: 'error', message: String(error?.message || error || 'Scores could not be saved.') });
      return false;
    }
  }, [authUser?.token, meetingId, selectedAgendaItem?.id, selectedContestType, url]);

  useEffect(() => {
    if (!selectedContestType) return;
    setActiveAgendaIndexByContest((current) => {
      const previous = Number.isFinite(current[selectedContestType]) ? current[selectedContestType] : 0;
      const maxIndex = Math.max(0, selectedContestAgendaItems.length - 1);
      if (previous <= maxIndex) return current;
      return { ...current, [selectedContestType]: maxIndex };
    });
  }, [selectedContestAgendaItems.length, selectedContestType]);

  useEffect(() => {
    if (!show) {
      setPanelView('speakers');
      setBallotValues({});
      ballotValuesRef.current = {};
      setScoreSaveState({ status: 'idle', message: '' });
    }
  }, [show]);

  useEffect(() => {
    setScoreSaveState({ status: 'idle', message: '' });
  }, [selectedAgendaItem?.id, selectedContestType]);

  useEffect(() => {
    setResultsOrderByContest({});
  }, [authUser]);

  useEffect(() => {
    if (!show || !meetingId || !selectedContestType) return;
    const userId = String(authUser?.user?._id || authUser?.user?.id || authUser?.user?.tmAuthUserId || authUser?._id || authUser?.tmAuthUserId || '').trim();
    const storageKey = getStorageKeyForOrder(meetingId, selectedContestType, userId);
    if (!storageKey) return;

    try {
      const storedRaw = localStorage.getItem(storageKey);
      if (storedRaw) {
        const parsed = JSON.parse(storedRaw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setResultsOrderByContest((current) => ({
            ...current,
            [selectedContestType]: parsed
          }));
        }
      }
    } catch (_) {}
  }, [authUser, meetingId, selectedContestType, show]);

  useEffect(() => {
    loadScoresForActiveAgenda();
  }, [loadScoresForActiveAgenda]);

  const goPrevContestType = async () => {
    if (!hasContestTypePager) return;
    if (!await saveScoresForActiveAgenda({ showStatus: true })) return;
    setActiveContestTypeIndex((current) => (current === 0 ? assignedContestEntries.length - 1 : current - 1));
  };

  const goNextContestType = async () => {
    if (!hasContestTypePager) return;
    if (!await saveScoresForActiveAgenda({ showStatus: true })) return;
    setActiveContestTypeIndex((current) => (current + 1) % assignedContestEntries.length);
  };

  const goPrevAgendaItem = async () => {
    if (!hasAgendaPager || !selectedContestType) return;
    if (!await saveScoresForActiveAgenda({ showStatus: true })) return;
    setActiveAgendaIndexByContest((current) => {
      const currentIndex = Number.isFinite(current[selectedContestType]) ? current[selectedContestType] : 0;
      return {
        ...current,
        [selectedContestType]: currentIndex === 0 ? selectedContestAgendaItems.length - 1 : currentIndex - 1
      };
    });
  };

  const goNextAgendaItem = useCallback(async () => {
    if (!hasAgendaPager || !selectedContestType) return;
    if (!await saveScoresForActiveAgenda({ showStatus: true })) return;
    setActiveAgendaIndexByContest((current) => {
      const currentIndex = Number.isFinite(current[selectedContestType]) ? current[selectedContestType] : 0;
      return {
        ...current,
        [selectedContestType]: (currentIndex + 1) % selectedContestAgendaItems.length
      };
    });
  }, [hasAgendaPager, saveScoresForActiveAgenda, selectedContestAgendaItems.length, selectedContestType]);

  useEffect(() => {
    if (!show || !hasAgendaPager) return undefined;
    const handlePointerUp = (event) => {
      const nextButton = nextAgendaButtonRef.current;
      if (!nextButton) return;
      const bounds = nextButton.getBoundingClientRect();
      const isInsideNextButton = event.clientX >= bounds.left
        && event.clientX <= bounds.right
        && event.clientY >= bounds.top
        && event.clientY <= bounds.bottom;
      if (!isInsideNextButton) return;
      event.preventDefault();
      void goNextAgendaItem();
    };

    window.addEventListener('pointerup', handlePointerUp, true);
    return () => window.removeEventListener('pointerup', handlePointerUp, true);
  }, [goNextAgendaItem, hasAgendaPager, show]);

  const updateBallotValue = (categoryId, criterionId, nextValue, maximumAllowed = null) => {
    if (!selectedContestType || !selectedAgendaItem?.id) return;
    const normalized = String(nextValue ?? '').trim();
    if (!/^\d*$/.test(normalized)) return;
    const parsed = normalized === '' ? null : Number(normalized);
    if (parsed !== null && !Number.isFinite(parsed)) return;
    const max = Number.isFinite(Number(maximumAllowed)) ? Number(maximumAllowed) : null;
    const clamped = parsed === null
      ? ''
      : String(Math.max(0, max === null ? parsed : Math.min(parsed, max)));
    const key = buildBallotKey(selectedContestType, selectedAgendaItem.id, categoryId, criterionId);
    setBallotValues((current) => {
      const next = { ...current, [key]: clamped };
      ballotValuesRef.current = next;
      return next;
    });
    setScoreSaveState({ status: 'idle', message: '' });
  };

  const closeJudgeModal = async () => {
    if (isMeetingLocked) {
      onClose();
      return;
    }
    if (panelView === 'speakers' && selectedContestType && selectedAgendaItem?.id) {
      if (!await saveScoresForActiveAgenda({ showStatus: true })) return;
    }
    onClose();
  };

  const getCategoryTotals = useCallback((category, categoryId) => {
    const criteria = Array.isArray(category?.criteria) ? category.criteria : [];
    const maximum = criteria.reduce((total, criterion) => {
      const bands = (Array.isArray(criterion?.ratingBands) ? criterion.ratingBands : [])
        .map((band) => Number(band?.maximumValue ?? 0))
        .filter((value) => Number.isFinite(value));
      const maxForCriterion = bands.length > 0 ? Math.max(...bands) : 0;
      return total + maxForCriterion;
    }, 0);

    const earned = criteria.reduce((total, criterion, criterionIndex) => {
      const criterionId = String(criterion?._id || criterion?.name || `criterion-${criterionIndex}`);
      const key = buildBallotKey(selectedContestType, selectedAgendaItem?.id || '', categoryId, criterionId);
      const value = scoreByCriterionKey[key];
      return total + (Number.isFinite(value) ? value : 0);
    }, 0);

    return { earned, maximum };
  }, [scoreByCriterionKey, selectedAgendaItem?.id, selectedContestType]);

  const getCategoryBandLabels = (category) => {
    const criteria = Array.isArray(category?.criteria) ? category.criteria : [];
    const allBands = criteria
      .flatMap((criterion) => (Array.isArray(criterion?.ratingBands) ? criterion.ratingBands : []))
      .map((band, index) => ({
        label: String(band?.label || '').trim(),
        displayOrder: Number(band?.displayOrder ?? index)
      }))
      .filter((band) => Boolean(band.label));

    const uniqueByLabel = new Map();
    allBands.forEach((band) => {
      const key = band.label.toLowerCase();
      const existing = uniqueByLabel.get(key);
      if (!existing || band.displayOrder < existing.displayOrder) {
        uniqueByLabel.set(key, band);
      }
    });

    const labels = Array.from(uniqueByLabel.values())
      .sort((left, right) => left.displayOrder - right.displayOrder)
      .map((band) => band.label);

    return labels;
  };

  const totalScore = useMemo(() => {
    if (!selectedContestType || !selectedAgendaItem?.id) return { earned: 0, maximum: 0, percentage: 0 };

    const categorySummaries = ballotCategories.map((category, categoryIndex) => {
      const categoryId = String(category?._id || category?.name || `category-${categoryIndex}`);
      return getCategoryTotals(category, categoryId);
    });

    const earned = categorySummaries.reduce((sum, entry) => sum + Number(entry?.earned || 0), 0);
    const maximum = categorySummaries.reduce((sum, entry) => sum + Number(entry?.maximum || 0), 0);
    const percentage = maximum > 0 ? Math.round((earned / maximum) * 100) : 0;

    return { earned, maximum, percentage };
  }, [ballotCategories, getCategoryTotals, selectedAgendaItem?.id, selectedContestType]);

  const resultsByAgendaId = useMemo(() => {
    if (!selectedContestType || selectedContestAgendaItems.length === 0) return {};

    return selectedContestAgendaItems.reduce((acc, agendaItem) => {
      const summary = ballotCategories.reduce((totals, category, categoryIndex) => {
        const categoryId = String(category?._id || category?.name || `category-${categoryIndex}`);
        const criteria = Array.isArray(category?.criteria) ? category.criteria : [];

        const categoryMaximum = criteria.reduce((sum, criterion) => {
          const bands = (Array.isArray(criterion?.ratingBands) ? criterion.ratingBands : [])
            .map((band) => Number(band?.maximumValue ?? 0))
            .filter((value) => Number.isFinite(value));
          return sum + (bands.length > 0 ? Math.max(...bands) : 0);
        }, 0);

        const categoryEarned = criteria.reduce((sum, criterion, criterionIndex) => {
          const criterionId = String(criterion?._id || criterion?.name || `criterion-${criterionIndex}`);
          const key = buildBallotKey(selectedContestType, agendaItem.id, categoryId, criterionId);
          const value = scoreByCriterionKey[key];
          return sum + (Number.isFinite(value) ? value : 0);
        }, 0);

        return {
          earned: totals.earned + categoryEarned,
          maximum: totals.maximum + categoryMaximum,
        };
      }, { earned: 0, maximum: 0 });

      acc[agendaItem.id] = {
        earned: summary.earned,
        maximum: summary.maximum,
        percentage: summary.maximum > 0 ? Math.round((summary.earned / summary.maximum) * 100) : 0,
      };

      return acc;
    }, {});
  }, [ballotCategories, scoreByCriterionKey, selectedContestAgendaItems, selectedContestType]);

  const orderedResultsAgendaItems = useMemo(() => {
    const availableIds = selectedContestAgendaItems.map((entry) => entry.id);
    const preferredOrder = Array.isArray(resultsOrderByContest[selectedContestType])
      ? resultsOrderByContest[selectedContestType]
      : [];
    const scoreSortedIds = selectedContestAgendaItems
      .map((entry, index) => ({ id: entry.id, index, score: Number(resultsByAgendaId[entry.id]?.earned || 0) }))
      .sort((left, right) => right.score - left.score || left.index - right.index)
      .map((entry) => entry.id);
    const orderedIds = preferredOrder.length > 0
      ? [...preferredOrder.filter((id) => availableIds.includes(id)), ...scoreSortedIds.filter((id) => !preferredOrder.includes(id))]
      : scoreSortedIds;
    const byId = new Map(selectedContestAgendaItems.map((entry) => [entry.id, entry]));
    return orderedIds.map((id) => byId.get(id)).filter(Boolean);
  }, [resultsByAgendaId, resultsOrderByContest, selectedContestAgendaItems, selectedContestType]);

  const reorderResults = (draggedId, targetId) => {
    if (!selectedContestType || !draggedId || !targetId || draggedId === targetId) return;
    setResultsOrderByContest((current) => {
      const baseOrder = Array.isArray(current[selectedContestType])
        ? [...current[selectedContestType]]
        : orderedResultsAgendaItems.map((entry) => entry.id);
      const fromIndex = baseOrder.indexOf(draggedId);
      const toIndex = baseOrder.indexOf(targetId);
      if (fromIndex < 0 || toIndex < 0) return current;
      const [moved] = baseOrder.splice(fromIndex, 1);
      baseOrder.splice(toIndex, 0, moved);

      const userId = String(authUser?.user?._id || authUser?.user?.id || authUser?.user?.tmAuthUserId || authUser?._id || authUser?.tmAuthUserId || '').trim();
      const storageKey = getStorageKeyForOrder(meetingId, selectedContestType, userId);
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(baseOrder));
        } catch (_) {}
      }

      return { ...current, [selectedContestType]: baseOrder };
    });
  };
  const resultsSort = useSortableList({
    disabled: isMeetingLocked,
    onMove: (from, to) => reorderResults(orderedResultsAgendaItems[from]?.id, orderedResultsAgendaItems[to]?.id),
  });

  const submitTopThree = async () => {
    if (!authUser?.token || !meetingId || !selectedContestType) return;
    const orderedAgendaItemIds = orderedResultsAgendaItems.map((entry) => entry.id);
    const submittedAgendaItemIds = isTiebreakerPanel ? orderedAgendaItemIds : orderedAgendaItemIds.slice(0, 3);
    const rankedSpeakers = orderedResultsAgendaItems
      .slice(0, submittedAgendaItemIds.length)
      .map((entry, index) => ({
        name: entry.speakerName,
        speechName: entry.speechTitle,
        place: index + 1
      }));
    if (submittedAgendaItemIds.length === 0) {
      setRankingSubmitState({ status: 'error', message: 'Add at least one ranked speaker before submitting.' });
      return;
    }

    setRankingSubmitState({ status: 'saving', message: '' });
    try {
      const submissionEndpoint = isTiebreakerPanel ? 'tiebreaker-ranking' : 'rankings';
      const response = await fetch(`${url}/contesthub/meetings/${meetingId}/${submissionEndpoint}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${authUser.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contestType: selectedContestType,
          rankedSpeakers,
          ...(isTiebreakerPanel ? { orderedAgendaItemIds: submittedAgendaItemIds } : { topAgendaItemIds: submittedAgendaItemIds })
        })
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.Error || response.statusText || 'Failed to submit ranking')
      }

      const userId = String(authUser?.user?._id || authUser?.user?.id || authUser?.user?.tmAuthUserId || authUser?._id || authUser?.tmAuthUserId || '').trim();
      const storageKey = getStorageKeyForOrder(meetingId, selectedContestType, userId);
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(orderedAgendaItemIds));
        } catch (_) {}
      }

      setRankingSubmitted(true);
      setRankingSubmitState({ status: 'success', message: isTiebreakerPanel ? 'Ranking submitted.' : 'Top 3 submitted.' });
    } catch (error) {
      setRankingSubmitState({ status: 'error', message: String(error?.message || error) });
    }
  };

  const headerMeta = panelView === 'results'
    ? (
      <div className="contest-judge-header-meta contest-judge-header-meta--results">
        <h3 className="contest-judge-results-title">{isTiebreakerPanel ? 'Tiebreaker Results' : 'Results'}</h3>
        <button type="button" className="contest-judge-results-back-btn" onClick={() => setPanelView('speakers')}>
          Back To Speakers
        </button>
      </div>
      )
    : (
      <div className="contest-judge-header-meta">
        {selectedContestType && selectedContestAgendaItems.length > 0 && selectedAgendaItem && (
          <>
            <div className="contest-judge-header-main">
              <div className="contest-judge-speaker-block">
                <div className="contest-judge-speaker contest-judge-speaker--header">
                  <div className="contest-judge-avatar">
                    {selectedAgendaItem.avatarSrc
                      ? <img src={selectedAgendaItem.avatarSrc} alt="Assigned speaker avatar" className="contest-judge-avatar-image" />
                      : selectedAgendaItem.initials
                        ? <span className="contest-judge-avatar-fallback">{selectedAgendaItem.initials}</span>
                        : <i className="bi bi-person-fill contest-judge-avatar-fallback"></i>}
                  </div>
                  <div>
                    <div className="contest-judge-speaker-name">{selectedAgendaItem.speakerName}</div>
                    <div className="contest-judge-speech-title">{selectedAgendaItem.speechTitle}</div>
                  </div>
                </div>
              </div>
              <div className="contest-judge-score-circles contest-judge-score-circles--header">
                <div
                  className="contest-judge-timer"
                  style={{
                    '--timer-pct': `${timerProgress}%`,
                    '--timer-color': timerColors.background,
                    '--timer-foreground': timerColors.foreground,
                    '--timer-ring-background': timerRingBackground
                  }}
                >
                  <div className="contest-judge-timer-inner">
                    <div className="contest-judge-timer-value">{formattedTimer}</div>
                  </div>
                </div>
                <div className="contest-judge-total" style={{ '--pct': `${totalScore.percentage}%` }}>
                  <div className="contest-judge-total-inner">
                    <div className="contest-judge-total-value">{totalScore.percentage}%</div>
                    <div className="contest-judge-total-label">Total</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="contest-judge-header-nav-row">
              <div className="contest-judge-header-nav-spacer" aria-hidden="true"></div>
              {hasAgendaPager ? (
                <div className="contest-judge-nav contest-judge-nav--compact contest-judge-nav--under contest-judge-nav--header-center">
                  <button
                    type="button"
                    className="contest-judge-nav-btn"
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={goPrevAgendaItem}
                    aria-label="Previous agenda item"
                  >
                    <i className="bi bi-chevron-left" />
                  </button>
                  <span className="contest-judge-nav-count">
                    {activeAgendaIndex + 1} / {selectedContestAgendaItems.length}
                  </span>
                  <button
                    ref={nextAgendaButtonRef}
                    type="button"
                    className="contest-judge-nav-btn contest-judge-nav-btn--next-agenda"
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      if (event.detail === 0) goNextAgendaItem();
                    }}
                    aria-label="Next agenda item"
                  >
                    <i className="bi bi-chevron-right" />
                  </button>
                </div>
              ) : <div className="contest-judge-header-nav-empty" aria-hidden="true"></div>}
              <div className="contest-judge-header-nav-actions">
                <button type="button" className="contest-judge-results-btn" onClick={async () => {
                  if (await saveScoresForActiveAgenda({ showStatus: true })) setPanelView('results');
                }}>
                  Results
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      );

  const footerMeta = (
    <div className="contest-judge-contest-head contest-judge-contest-head--footer">
      {hasContestTypePager && (
        <div className="contest-judge-nav contest-judge-nav--compact contest-judge-nav--under">
          <button
            type="button"
            className="contest-judge-nav-btn"
            onClick={goPrevContestType}
            aria-label="Previous contest type"
          >
            <i className="bi bi-chevron-left" />
          </button>
          <span className="contest-judge-nav-count">
            {activeContestTypeIndex + 1} / {assignedContestEntries.length}
          </span>
          <button
            type="button"
            className="contest-judge-nav-btn"
            onClick={goNextContestType}
            aria-label="Next contest type"
          >
            <i className="bi bi-chevron-right" />
          </button>
        </div>
      )}
      <div className="contest-judge-contest-name">{selectedContestType ? contestTypeNameFor(selectedContestType) : ''}</div>
    </div>
  );

  const speakerModalBody = (
    <div className="contest-judge-modal">
      <div className="contest-judge-body">
        {!selectedContestType && <p className="text-muted mb-0">No contest type is assigned to you for this meeting.</p>}
        {selectedContestType && selectedContestAgendaItems.length === 0 && <p className="text-muted mb-0">No agenda items are assigned to this contest yet.</p>}
        {selectedContestType && selectedContestAgendaItems.length > 0 && selectedAgendaItem && (
          <div className="contest-judge-view">
            <div className="contest-judge-ballot">
              {ballotCategories.length === 0 ? <p className="text-muted mb-0">No ballot schema is configured for this contest type yet.</p> : ballotCategories.map((category, categoryIndex) => {
                const criteria = Array.isArray(category?.criteria) ? category.criteria : [];
                const categoryId = String(category?._id || category?.name || `category-${categoryIndex}`);
                const { earned, maximum } = getCategoryTotals(category, categoryId);
                const categoryBandLabels = getCategoryBandLabels(category);
                return (
                  <div className="contest-judge-category" key={categoryId}>
                    <div className="contest-judge-category-title">{`${String(category?.name || `Category ${categoryIndex + 1}`)} (${earned}/${maximum})`}</div>
                    {criteria.length === 0 ? <p className="text-muted mb-2">No criteria in this category.</p> : criteria.map((criterion, criterionIndex) => {
                      const criterionId = String(criterion?._id || criterion?.name || `criterion-${criterionIndex}`);
                      const key = buildBallotKey(selectedContestType, selectedAgendaItem.id, categoryId, criterionId);
                      const bands = (Array.isArray(criterion?.ratingBands) ? criterion.ratingBands : [])
                        .slice()
                        .sort((left, right) => Number(left?.displayOrder ?? 0) - Number(right?.displayOrder ?? 0));
                      const excellentBand = bands.find((band) => String(band?.label || '').trim().toLowerCase() === 'excellent') || null;
                      const criterionMax = Number(excellentBand?.maximumValue);
                      const maximumAllowed = Number.isFinite(criterionMax)
                        ? criterionMax
                        : Math.max(0, ...bands.map((band) => Number(band?.maximumValue ?? 0)).filter((value) => Number.isFinite(value)));
                      const rangeByLabel = new Map(
                        bands.map((band) => {
                          const minValue = band?.minimumValue;
                          const maxValue = band?.maximumValue;
                          let range = '-';
                          if (minValue !== null && minValue !== undefined && maxValue !== null && maxValue !== undefined) {
                            range = `${minValue}-${maxValue}`;
                          } else if (maxValue !== null && maxValue !== undefined) {
                            range = String(maxValue);
                          } else if (minValue !== null && minValue !== undefined) {
                            range = String(minValue);
                          }
                          return [String(band?.label || '').trim().toLowerCase(), range];
                        })
                      );
                      return (
                        <Row className="contest-judge-grid-row" key={criterionId}>
                          <Col xs={12} lg={6}>
                            <div className="contest-judge-criterion-heading">
                              <div className="contest-judge-criterion">{String(criterion?.name || `Criterion ${criterionIndex + 1}`)}</div>
                              <button
                                type="button"
                                className="contest-judge-info-btn"
                                tabIndex={-1}
                                onClick={() => setInfoCriterion({
                                  name: String(criterion?.name || `Criterion ${criterionIndex + 1}`),
                                  description: String(criterion?.description || '').trim()
                                })}
                                aria-label={`Show information for ${String(criterion?.name || `Criterion ${criterionIndex + 1}`)}`}
                                title="Show criterion description"
                              >
                                <i className="bi bi-info-lg" aria-hidden="true" />
                              </button>
                            </div>
                            {criterion?.subtitle ? <div className="contest-judge-criterion-subtitle">{String(criterion.subtitle)}</div> : null}
                          </Col>
                          <Col xs={12} lg={6}>
                            <div className="contest-judge-score-band-labels">
                              <span>Score</span>
                              {categoryBandLabels.map((label) => <span key={`${criterionId}-head-${label}`}>{label}</span>)}
                            </div>
                            <div className="contest-judge-score-band-row">
                              <div className="contest-judge-score-wrap">
                                <Form.Control
                                  className="contest-judge-score-input"
                                  type="text"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  value={ballotValues[key] || ''}
                                  onChange={(event) => updateBallotValue(categoryId, criterionId, event.target.value, maximumAllowed)}
                                  onBlur={() => { void saveScoresForActiveAgenda(); }}
                                />
                              </div>
                              {categoryBandLabels.map((label) => <div className="contest-judge-band" key={`${criterionId}-band-${label}`}>{rangeByLabel.get(label.toLowerCase()) || '-'}</div>)}
                            </div>
                          </Col>
                        </Row>
                      );
                    })}
                  </div>
                );
              })}
              {ballotCategories.length > 0 ? <div className="contest-judge-save-row">
                <button
                  type="button"
                  className="contest-judge-save-btn"
                  onClick={() => { void saveScoresForActiveAgenda({ showStatus: true }); }}
                  disabled={scoreSaveState.status === 'saving' || isMeetingLocked}
                >
                  {scoreSaveState.status === 'saving' ? 'Saving...' : 'Save'}
                </button>
                {scoreSaveState.message ? <div className={`contest-judge-save-msg is-${scoreSaveState.status}`} role={scoreSaveState.status === 'error' ? 'alert' : 'status'}>{scoreSaveState.message}</div> : null}
              </div> : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const resultsModalBody = (
    <div className="contest-judge-modal">
      <div className="contest-judge-body">
        {!selectedContestType && <p className="text-muted mb-0">No contest type is assigned to you for this meeting.</p>}
        {selectedContestType && selectedContestAgendaItems.length === 0 && <p className="text-muted mb-0">No agenda items are assigned to this contest yet.</p>}
        {selectedContestType && selectedContestAgendaItems.length > 0 && (
          <div className="contest-judge-results-view">
            <div className="contest-judge-results-subtitle">{contestTypeNameFor(selectedContestType)}</div>
            <div className="contest-judge-results-list" {...resultsSort.containerProps}>
              {orderedResultsAgendaItems.map((agendaItem, index) => {
                const summary = resultsByAgendaId[agendaItem.id] || { earned: 0, maximum: 0, percentage: 0 };
                const isActive = agendaItem.id === selectedAgendaItem?.id;
                return (
                  <div
                    key={agendaItem.id}
                    className={`contest-judge-results-item${isActive ? ' is-active' : ''}`}
                    {...resultsSort.itemProps(index)}
                    {...resultsSort.handleProps(index)}
                  >
                    <span className="contest-judge-results-item-order">{index + 1}.</span>
                    <div className="contest-judge-avatar contest-judge-results-item-avatar">
                      {agendaItem.avatarSrc
                        ? <img src={agendaItem.avatarSrc} alt={`Avatar for ${agendaItem.speakerName}`} className="contest-judge-avatar-image" />
                        : agendaItem.initials
                          ? <span className="contest-judge-avatar-fallback">{agendaItem.initials}</span>
                          : <i className="bi bi-person-fill contest-judge-avatar-fallback"></i>}
                    </div>
                    <div className="contest-judge-results-item-meta">
                      <span className="contest-judge-results-item-speaker">{agendaItem.speakerName}</span>
                      <span className="contest-judge-results-item-title">{agendaItem.speechTitle}</span>
                    </div>
                    <strong className="contest-judge-results-item-score">{summary.earned}/{summary.maximum} ({summary.percentage}%)</strong>
                    <button
                      type="button"
                      className="contest-judge-results-open-btn"
                      onClick={() => {
                        const nextIndex = selectedContestAgendaItems.findIndex((entry) => entry.id === agendaItem.id);
                        if (nextIndex >= 0) {
                          setActiveAgendaIndexByContest((current) => ({ ...current, [selectedContestType]: nextIndex }));
                        }
                        setPanelView('speakers');
                      }}
                      aria-label={`Open scoring sheet for ${agendaItem.speakerName}`}
                    >
                      Open
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="contest-judge-results-submit-row">
              <button
                type="button"
                className="contest-judge-results-submit-btn"
                onClick={submitTopThree}
                disabled={rankingSubmitted || rankingSubmitState.status === 'saving'}
              >
                {rankingSubmitted
                  ? (isTiebreakerPanel ? 'Ranking Submitted' : 'Top 3 Submitted')
                  : rankingSubmitState.status === 'saving'
                    ? 'Submitting...'
                    : (isTiebreakerPanel ? 'Submit Ranking' : 'Submit Top 3')}
              </button>
              {rankingSubmitState.message && (
                <div className={`contest-judge-results-submit-msg is-${rankingSubmitState.status}`}>
                  {rankingSubmitState.message}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <StandardModal
        show={show}
        handleClose={() => { void closeJudgeModal(); }}
        title=" "
        headerMeta={headerMeta}
        bodyClassName="contesthub-judge-modal"
        footerMeta={footerMeta}
        fullscreen
        hideLogo
        hideCollapse
        ModalBodyComponent={(
          <fieldset disabled={isMeetingLocked} style={{ border: 'none', padding: 0, margin: 0, display: 'contents' }}>
            {panelView === 'results' ? resultsModalBody : speakerModalBody}
          </fieldset>
        )}
      />
      <StandardModal
        show={Boolean(infoCriterion)}
        handleClose={() => setInfoCriterion(null)}
        title={infoCriterion?.name || 'Criterion information'}
        style={{ maxWidth: '560px', marginTop: '12vh' }}
        bodyClassName="contesthub-criterion-info-modal"
        ModalBodyComponent={<p className="contest-judge-criterion-info-text">{infoCriterion?.description || 'No description is available for this criterion.'}</p>}
        ModalFooterComponent={<Button variant="secondary" onClick={() => setInfoCriterion(null)}>Close</Button>}
      />
    </>
  );
}
